import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import {
    FaCalendarAlt,
    FaUser,
    FaPhoneAlt,
    FaEnvelope,
    FaSpa,
    FaClock,
    FaUserTie,
    FaNotesMedical,
    FaCheckCircle,
    FaExclamationTriangle,
    FaHandshake,
    FaCopy
} from 'react-icons/fa';
import './OnlineBooking.css';

interface OnlineBookingSettings {
    title: string;
    description: string;
    fields: {
        customerName: { label: string; placeholder: string };
        phone: { label: string; placeholder: string };
        email: { label: string; placeholder: string };
        service: { label: string; placeholder: string };
        date: { label: string; placeholder: string };
        time: { label: string; placeholder: string };
        staff: { label: string; placeholder: string };
        referrer: { label: string; placeholder: string; visible: boolean };
        notes: { label: string; placeholder: string };
    };
    footerText: string;
}

interface Service {
    id: string;          // MongoDB _id is a string
    name: string;
    price: number;
    color: string;
    duration?: number;
    description?: string;
}

interface Employee {
    id: string;          // MongoDB _id is a string
    name: string;
    role: string;
    status: string;
}

const OnlineBooking: React.FC = () => {
    const { t, language } = useLanguage();

    // Get booking code from URL params (route: /book/:code)
    const { code } = useParams<{ code: string }>();

    // Form state — serviceIds are strings (MongoDB _id)
    const [formData, setFormData] = useState({
        customerName: '',
        phone: '',
        email: '',
        serviceIds: [] as string[],
        preferredDate: new Date().toISOString().split('T')[0],
        preferredTime: '09:00',
        preferredStaffId: '',
        referrerCode: '',
        notes: ''
    });

    // Data state
    const [businessName, setBusinessName] = useState('');
    const [businessPhone, setBusinessPhone] = useState('');
    const [businessLogo, setBusinessLogo] = useState<string | null>(null);
    const [services, setServices] = useState<Service[]>([]);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [settings, setSettings] = useState<OnlineBookingSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [linkCopied, setLinkCopied] = useState(false);
    
    // File upload state
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [previewUrls, setPreviewUrls] = useState<string[]>([]);

    // Fetch data using the public API (no auth needed)
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                if (!code) {
                    setError('Invalid booking link. No business code provided.');
                    setLoading(false);
                    return;
                }

                // Use public API - no authentication required
                const response = await apiService.getPublicBookingPage(code);

                if (response.success && response.data) {
                    const { business, services: svcList, employees: empList } = response.data;
                    setBusinessName(business.name || 'Online Booking');
                    setBusinessPhone(business.phone || '');
                    setBusinessLogo(business.settings?.logo || null);
                    // Normalize service IDs to strings, price to number
                    const normalizedServices: Service[] = (svcList || []).map((s: any) => ({
                        id: String(s._id || s.id || ''),
                        name: s.name || '',
                        price: Number(s.price) || 0,
                        color: s.color || '#cccccc',
                        duration: s.duration,
                        description: s.description
                    }));
                    // Normalize employee IDs to strings
                    const normalizedEmployees: Employee[] = (empList || []).map((e: any) => ({
                        id: String(e._id || e.id || ''),
                        name: e.name || '',
                        role: e.role || '',
                        status: e.status || 'active'
                    }));
                    setServices(normalizedServices);
                    setEmployees(normalizedEmployees);

                    // If business has booking settings in its settings object, use those
                    if (business.settings?.bookingPage) {
                        setSettings(business.settings.bookingPage);
                    }
                } else {
                    setError('Business not found. Please check the link.');
                }

                setLoading(false);
            } catch (err: any) {
                console.error('Error fetching data:', err);
                setError(err.message || 'Could not load booking system. Please refresh and try again.');
                setLoading(false);
            }
        };

        fetchData();
    }, [code]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const toggleService = (serviceId: string) => {
        setFormData(prev => {
            const currentIds = prev.serviceIds;
            if (currentIds.includes(serviceId)) {
                return { ...prev, serviceIds: currentIds.filter(id => id !== serviceId) };
            } else {
                return { ...prev, serviceIds: [...currentIds, serviceId] };
            }
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            // Limit to max 3 images total
            const newTotal = selectedFiles.length + files.length;
            if (newTotal > 3) {
                setError('You can only upload up to 3 images.');
                return;
            }

            const validFiles = files.filter(f => f.type.startsWith('image/') && f.size <= 8 * 1024 * 1024);
            if (validFiles.length < files.length) {
                setError('Some files were rejected. Only images under 8MB are allowed.');
            }

            const newFiles = [...selectedFiles, ...validFiles].slice(0, 3);
            setSelectedFiles(newFiles);
            
            // Create preview URLs
            const newUrls = validFiles.map(file => URL.createObjectURL(file));
            setPreviewUrls([...previewUrls, ...newUrls].slice(0, 3));
        }
    };

    const removeFile = (index: number) => {
        const newFiles = [...selectedFiles];
        newFiles.splice(index, 1);
        setSelectedFiles(newFiles);

        const newUrls = [...previewUrls];
        URL.revokeObjectURL(newUrls[index]);
        newUrls.splice(index, 1);
        setPreviewUrls(newUrls);
    };

    const calculateTotalPrice = () => {
        return formData.serviceIds.reduce((total, id) => {
            const service = services.find(s => s.id === id);
            return total + (Number(service?.price) || 0);
        }, 0);
    };

    const validateForm = (): boolean => {
        if (!formData.customerName.trim()) {
            setError('Please enter your full name');
            return false;
        }
        if (!formData.phone.trim()) {
            setError('Please enter your phone number');
            return false;
        }
        if (formData.serviceIds.length === 0) {
            setError('Please select at least one service');
            return false;
        }
        if (!formData.preferredDate) {
            setError('Please select a preferred date');
            return false;
        }
        if (!formData.preferredTime) {
            setError('Please select a preferred time');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) return;
        if (!code) return;

        setSubmitting(true);

        try {
            const selectedServices = services.filter(s => formData.serviceIds.includes(s.id));
            const selectedStaff = formData.preferredStaffId
                ? employees.find(e => e.id === formData.preferredStaffId)
                : null;

            const bookingData = {
                customerName: formData.customerName.trim(),
                phone: formData.phone.trim(),
                email: formData.email.trim(),
                service: selectedServices.map(s => s.name).join(', '),
                serviceId: selectedServices[0]?.id,
                serviceIds: formData.serviceIds,
                services: selectedServices.map(s => s.name),
                date: formData.preferredDate,
                time: formData.preferredTime,
                staffId: selectedStaff?.id,
                staffName: selectedStaff?.name,
                notes: formData.notes.trim(),
                referrerCode: formData.referrerCode.trim(),
                source: 'online' as const,
                status: 'pending',
                noteImages: [] as string[]
            };

            if (selectedFiles.length > 0) {
                try {
                    const uploadRes = await apiService.uploadBookingImages(selectedFiles);
                    if (uploadRes.success) {
                        bookingData.noteImages = uploadRes.imageUrls;
                    }
                } catch (err) {
                    console.error('Image upload failed', err);
                }
            }

            // Use public API - no auth required!
            await apiService.createPublicBooking(code, bookingData);

            setSubmitted(true);

        } catch (err: any) {
            console.error('Error creating booking:', err);
            setError(err.message || 'Could not complete your booking. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            customerName: '',
            phone: '',
            email: '',
            serviceIds: [],
            preferredDate: new Date().toISOString().split('T')[0],
            preferredTime: '09:00',
            preferredStaffId: '',
            referrerCode: '',
            notes: ''
        });
        setSelectedFiles([]);
        setPreviewUrls([]);
        setSubmitted(false);
        setError(null);
    };

    // Generate time slots (15 min increments)
    const timeSlots = [];
    for (let hour = 8; hour <= 20; hour++) {
        const hourStr = hour.toString().padStart(2, '0');
        timeSlots.push(`${hourStr}:00`);
        timeSlots.push(`${hourStr}:15`);
        timeSlots.push(`${hourStr}:30`);
        timeSlots.push(`${hourStr}:45`);
    }

    // Min date is today
    const today = new Date().toISOString().split('T')[0];

    // Copy link function
    const handleCopyLink = () => {
        const bookingUrl = window.location.href;
        navigator.clipboard.writeText(bookingUrl).then(() => {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        }).catch(err => {
            console.error('Failed to copy:', err);
            alert('Failed to copy link. Please copy manually: ' + bookingUrl);
        });
    };

    // Dynamic background style: use logo if available, else fallback gradient
    const pageStyle: React.CSSProperties = businessLogo
        ? {
            backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.65)), url('${businessLogo}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }
        : {};

    if (loading) {
        return (
            <div className={`online-booking-page ${businessLogo ? 'has-custom-bg' : 'default-bg'}`}>
                <div className="booking-container">
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading your experience...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className={`online-booking-page ${businessLogo ? 'has-custom-bg' : 'default-bg'}`} style={pageStyle}>
                <div className="booking-container">
                    <div className="success-state">
                        <FaCheckCircle className="success-icon" />
                        <h2>Booking Successful!</h2>
                        <p>Thank you for choosing {businessName}. Our team will contact you shortly to confirm your appointment.</p>
                        <div className="booking-summary">
                            <h4>Booking Summary:</h4>
                            <p><strong>Name:</strong> {formData.customerName}</p>
                            <p><strong>Phone:</strong> {formData.phone}</p>
                            <p><strong>Services:</strong> {services.filter(s => formData.serviceIds.includes(s.id)).map(s => s.name).join(', ')}</p>
                            <p><strong>Total Price:</strong> ${calculateTotalPrice()}</p>
                            <p><strong>Date & Time:</strong> {formData.preferredDate} at {formData.preferredTime}</p>
                            {formData.referrerCode && <p><strong>Referrer:</strong> {formData.referrerCode}</p>}
                        </div>
                        <button className="btn-primary" onClick={resetForm}>
                            Place Another Booking
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`online-booking-page ${businessLogo ? 'has-custom-bg' : 'default-bg'}`} style={pageStyle}>
            <div className="booking-container">
                <div className="booking-header">
                    <FaCalendarAlt className="header-icon" />
                    <h1>{businessName || settings?.title || 'Online Booking'}</h1>
                    <p>{settings?.description || 'Treat yourself to a premium experience. Book your appointment in seconds.'}</p>

                    {/* Copy Link Section */}
                    <div className="copy-link-section" style={{
                        marginTop: '1rem',
                        padding: '0.75rem 1rem',
                        background: 'rgba(102, 126, 234, 0.1)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        border: '1px solid rgba(102, 126, 234, 0.2)'
                    }}>
                        <span style={{
                            flex: 1,
                            fontSize: '0.85rem',
                            color: '#555',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {typeof window !== 'undefined' ? window.location.href : ''}
                        </span>
                        <button
                            onClick={handleCopyLink}
                            style={{
                                padding: '0.5rem 1rem',
                                background: linkCopied ? '#4caf50' : '#667eea',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                fontSize: '0.85rem',
                                fontWeight: '500',
                                transition: 'all 0.3s ease',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <FaCopy />
                            {linkCopied ? '✓ Copied!' : 'Copy Link'}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="error-banner">
                        <FaExclamationTriangle /> {error}
                    </div>
                )}

                <form className="booking-form" onSubmit={handleSubmit}>
                    <div className="form-section">
                        <h3><FaUser /> Customer Details</h3>

                        <div className="form-group">
                            <label htmlFor="customerName">
                                {settings?.fields?.customerName.label || 'Full Name'} <span className="required">*</span>
                            </label>
                            <div className="input-with-icon">
                                <FaUser className="input-icon" />
                                <input
                                    type="text"
                                    id="customerName"
                                    name="customerName"
                                    value={formData.customerName}
                                    onChange={handleInputChange}
                                    placeholder={settings?.fields?.customerName.placeholder || 'e.g. John Doe'}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="phone">
                                    {settings?.fields?.phone.label || 'Phone Number'} <span className="required">*</span>
                                </label>
                                <div className="input-with-icon">
                                    <FaPhoneAlt className="input-icon" />
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        placeholder={settings?.fields?.phone.placeholder || '+84...'}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">{settings?.fields?.email.label || 'Email Address'}</label>
                                <div className="input-with-icon">
                                    <FaEnvelope className="input-icon" />
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder={settings?.fields?.email.placeholder || 'john@example.com'}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3><FaSpa /> Choose Your Service</h3>

                        <div className="form-group">
                            <label>
                                {settings?.fields?.service.label || 'Select Services'} <span className="required">*</span>
                            </label>
                            {services.length > 0 ? (
                                <div className="services-selection-list">
                                    {services.map(service => (
                                        <div
                                            key={service.id}
                                            className={`service-selection-item ${formData.serviceIds.includes(service.id) ? 'selected' : ''}`}
                                            onClick={() => toggleService(service.id)}
                                        >
                                            <div className="service-selection-checkbox">
                                                {formData.serviceIds.includes(service.id) ? '✅' : '⬜'}
                                            </div>
                                            <div className="service-selection-info">
                                                <span className="service-selection-name">{service.name}</span>
                                                <span className="service-selection-price">${Number(service.price) || 0}</span>
                                            </div>
                                            <div className="service-selection-color" style={{ backgroundColor: service.color || '#ccc' }}></div>
                                        </div>
                                    ))}
                                    <div className="service-selection-total">
                                        <span>Total:</span>
                                        <span className="total-price">${calculateTotalPrice()}</span>
                                    </div>
                                </div>
                            ) : (
                                <p className="no-services-message">
                                    No services available currently. Please contact us directly.
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="form-section">
                        <h3><FaClock /> Date & Time</h3>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="preferredDate">
                                    {settings?.fields?.date.label || 'Preferred Date'} <span className="required">*</span>
                                </label>
                                <input
                                    type="date"
                                    id="preferredDate"
                                    name="preferredDate"
                                    value={formData.preferredDate}
                                    onChange={handleInputChange}
                                    min={today}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="preferredTime">
                                    {settings?.fields?.time.label || 'Preferred Time'} <span className="required">*</span>
                                </label>
                                <select
                                    id="preferredTime"
                                    name="preferredTime"
                                    value={formData.preferredTime}
                                    onChange={handleInputChange}
                                    required
                                >
                                    {timeSlots.map(time => (
                                        <option key={time} value={time}>{time}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3><FaUserTie /> Preference & Notes</h3>

                        <div className="form-group">
                            <label htmlFor="preferredStaffId">
                                {settings?.fields?.staff.label || 'Preferred Professional (Optional)'}
                            </label>
                            <div className="input-with-icon">
                                <FaUserTie className="input-icon" />
                                <select
                                    id="preferredStaffId"
                                    name="preferredStaffId"
                                    value={formData.preferredStaffId}
                                    onChange={handleInputChange}
                                >
                                    <option value="">{settings?.fields?.staff.placeholder || 'Any available professional'}</option>
                                    {employees.map(emp => (
                                        <option key={emp.id} value={emp.id}>{emp.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {settings?.fields?.referrer.visible && (
                            <div className="form-group">
                                <label htmlFor="referrerCode">
                                    {settings?.fields?.referrer.label || 'Referrer (Optional)'}
                                </label>
                                <div className="input-with-icon">
                                    <FaHandshake className="input-icon" />
                                    <input
                                        type="text"
                                        id="referrerCode"
                                        name="referrerCode"
                                        value={formData.referrerCode}
                                        onChange={handleInputChange}
                                        placeholder={settings?.fields?.referrer.placeholder || 'e.g. Friend, Facebook...'}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="notes">{settings?.fields?.notes.label || 'Special Requests'}</label>
                            <div className="input-with-icon area">
                                <FaNotesMedical className="input-icon" />
                                <textarea
                                    id="notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    placeholder={settings?.fields?.notes.placeholder || 'Tell us about any specific needs...'}
                                    rows={3}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>{language === 'vi' ? 'Đính kèm ảnh tham khảo (Tùy chọn)' : 'Attach Reference Photos (Optional)'}</label>
                            <div className="image-upload-area">
                                <input 
                                    type="file" 
                                    accept="image/*" 
                                    multiple 
                                    onChange={handleFileChange}
                                    id="booking-images"
                                    style={{ display: 'none' }}
                                />
                                <label htmlFor="booking-images" className="upload-btn" style={{ 
                                    display: 'inline-block',
                                    padding: '8px 16px',
                                    background: '#f1f5f9',
                                    color: '#475569',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    border: '1px dashed #cbd5e1',
                                    marginBottom: '10px'
                                }}>
                                    {language === 'vi' ? '+ Thêm Ảnh (Tối đa 3)' : '+ Add Photos (Max 3)'}
                                </label>

                                {previewUrls.length > 0 && (
                                    <div className="image-previews" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                        {previewUrls.map((url, idx) => (
                                            <div key={idx} style={{ position: 'relative', width: '80px', height: '80px' }}>
                                                <img 
                                                    src={url} 
                                                    alt="preview" 
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0' }} 
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeFile(idx)}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '-5px',
                                                        right: '-5px',
                                                        background: '#ef4444',
                                                        color: '#fff',
                                                        border: 'none',
                                                        borderRadius: '50%',
                                                        width: '20px',
                                                        height: '20px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        fontSize: '12px'
                                                    }}
                                                >×</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={submitting || services.length === 0}
                        >
                            {submitting
                                ? 'Securing your spot...'
                                : 'Confirm Appointment'
                            }
                        </button>
                    </div>
                </form>

                <div className="booking-footer">
                    <p>
                        {settings?.footerText || (
                            businessPhone
                                ? <>Need assistance? Call us: <strong>{businessPhone}</strong></>
                                : 'Need assistance? Please contact us directly.'
                        )}
                    </p>
                </div>

            </div>
        </div>
    );
};

// Error Boundary to prevent full blank screen on crashes
class OnlineBookingErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { hasError: boolean; errorMsg: string }
> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false, errorMsg: '' };
    }
    static getDerivedStateFromError(error: Error) {
        return { hasError: true, errorMsg: error?.message || 'Unknown error' };
    }
    componentDidCatch(error: Error, info: any) {
        console.error('OnlineBooking crash:', error, info);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f0c29', color: '#fff', padding: '2rem', flexDirection: 'column', gap: '1rem' }}>
                    <h2>⚠️ Something went wrong loading the booking page.</h2>
                    <p style={{ color: '#ccc', fontSize: '0.9rem' }}>{this.state.errorMsg}</p>
                    <button onClick={() => window.location.reload()} style={{ padding: '0.75rem 1.5rem', background: '#667eea', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem' }}>
                        🔄 Reload Page
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

const OnlineBookingWithBoundary: React.FC = () => (
    <OnlineBookingErrorBoundary>
        <OnlineBooking />
    </OnlineBookingErrorBoundary>
);

export default OnlineBookingWithBoundary;
